import { useEffect, useRef, useState } from 'react';
import type { Member } from '../types';
import { LOCALE_META } from '../lib/locales';
import { signOut } from '../lib/session';
import { setMyName } from '../lib/api';
import Avatar from './Avatar';
import LanguagePicker from './LanguagePicker';

/**
 * Who you are signed in as, and the two things that belong to you rather than
 * to the work: which languages you review, and leaving.
 *
 * Both used to sit at the bottom of the rail, which made them look like
 * navigation. They are settings, and settings belong behind the face in the
 * corner — which is also the one place still on screen once the rail becomes a
 * drawer on a phone.
 */
export default function ProfileMenu({
  member, name, locales, onLanguages, onRenamed,
}: {
  member: Member;
  /** Held by the workspace rather than read off member, so a rename shows
   *  everywhere at once without a reload. */
  name: string;
  /** What this person actually sees, which for an owner is every locale. */
  locales: string[];
  onLanguages: (langs: string[]) => void;
  onRenamed: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const isOwner = member.role === 'owner';
  const summary = locales.map((l) => LOCALE_META[l]?.native ?? l).join(' · ');

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Moves the caret into the panel, so a keyboard tabs through what just
  // appeared rather than continuing past it into the page.
  useEffect(() => { if (open) panel.current?.focus(); }, [open]);
  // Closing mid-rename should not leave a half-typed name waiting next time.
  useEffect(() => { if (!open) { setEditing(false); setError(null); } }, [open]);

  async function rename() {
    const next = draft.trim();
    if (!next || next === name) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      const row = await setMyName(next);
      onRenamed(row.display_name);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="profile" ref={root}>
      <button
        className="profile-trigger"
        ref={trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="profile-who">
          <span className="nm">{name}</span>
          <span className="sub">{summary || 'No languages yet'}</span>
        </span>
        <Avatar name={name} url={member.avatar_url} size={28} />
      </button>

      {open && (
        <div
          className="profile-pop" ref={panel} tabIndex={-1}
          role="dialog" aria-label="Your account"
        >
          <div className="profile-head">
            <Avatar name={name} url={member.avatar_url} size={38} />
            <span className="profile-id">
              <span className="nm">{name}</span>
              {member.email && <span className="em">{member.email}</span>}
            </span>
          </div>

          <div className="profile-grp">
            <span className="profile-label">Name the team sees</span>
            {editing ? (
              <>
                <input
                  className="profile-rename" autoFocus maxLength={60}
                  value={draft} disabled={busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void rename(); }
                    if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
                  }}
                />
                <span className="acts">
                  <button className="btn sm pri" disabled={!draft.trim() || busy}
                    onClick={() => void rename()}>
                    {busy ? 'Saving…' : 'Save name'}
                  </button>
                  <button className="btn sm" disabled={busy}
                    onClick={() => { setDraft(name); setEditing(false); setError(null); }}>
                    Cancel
                  </button>
                </span>
              </>
            ) : (
              <button className="btn sm" onClick={() => { setDraft(name); setEditing(true); }}>
                Change your name
              </button>
            )}
            {error && <span className="profile-err">{error}</span>}
            <span className="profile-note">
              This is the workspace's own copy — it does not follow your Google account, so
              changing it there has no effect here.
            </span>
          </div>

          <div className="profile-grp">
            <span className="profile-label">
              {isOwner ? 'Languages' : 'Languages you review'}
            </span>
            {locales.length > 0 ? (
              <span className="profile-langs">
                {locales.map((l) => (
                  <span key={l} className="pill p-acc">{LOCALE_META[l]?.native ?? l}</span>
                ))}
              </span>
            ) : (
              <span className="profile-note">
                None yet — pick the ones you can judge and the queues will fill up.
              </span>
            )}
            {isOwner ? (
              <span className="profile-note">
                You are the owner, so you see every language whatever this says.
              </span>
            ) : (
              <LanguagePicker current={locales} onSaved={onLanguages} />
            )}
          </div>

          <div className="profile-sep" />

          <button className="btn sm" onClick={() => void signOut()}>Sign out</button>
        </div>
      )}
    </div>
  );
}
